import React, { useState, useEffect } from 'react';
import FestivalAmbient from '../../components/festival/FestivalAmbient';
import useInactivityLogout from '../../hooks/useInactivityLogout';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  LayoutDashboard, ClipboardList, LogOut, MessageSquare,
  Package, ScanBarcode, Truck, FolderOpen, Users, Building2,
  BarChart3, GitBranch, Clock, Calendar, Layers, ShoppingCart,
  FileText, Inbox, Headphones, Settings, Menu, X, ChevronRight, CheckSquare,
  FileSpreadsheet, Paintbrush, Wallet, UsersRound, ClipboardCheck, Ruler, FileSignature, ScrollText
} from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';
import NotificationBell from '../../components/NotificationBell';

// Permission-gated menus — grouped for the drawer
const PERM_MENU_GROUPS = [
  {
    group: 'Core',
    menus: [
      { key: 'projects',         to: '/worker/projects',         label: 'Projects',       icon: FolderOpen },
      { key: 'main_po',          to: '/worker/main-po',          label: 'Manufacturing Order',        icon: FileSpreadsheet },
      { key: 'finish',           to: '/worker/finish',           label: 'Finish Sheets',  icon: Paintbrush },
      { key: 'quotations',       to: '/worker/quotations',       label: 'Quotations',     icon: FileSignature },
      { key: 'cash_book',        to: '/worker/cash-book',        label: 'Cash Book',      icon: Wallet },
      { key: 'payroll',          to: '/worker/payroll',          label: 'Payroll',        icon: UsersRound },
      { key: 'quality_report',   to: '/worker/quality',          label: 'Quality Reports',icon: ClipboardCheck },
      { key: 'cft',              to: '/worker/cft',              label: 'CFT Sheets',     icon: Ruler },
      { key: 'tasks',            to: '/worker/tasks-admin',      label: 'All Tasks',      icon: ClipboardList },
      { key: 'workers',          to: '/worker/workers',          label: 'Workers',        icon: Users },
      { key: 'departments',      to: '/worker/departments',      label: 'Departments',    icon: Building2 },
      { key: 'clients',          to: '/worker/clients',          label: 'Clients',        icon: Users },
    ]
  },
  {
    group: 'Reports & Tracking',
    menus: [
      { key: 'reports',          to: '/worker/reports',          label: 'Reports',        icon: BarChart3 },
      { key: 'product_tracking', to: '/worker/product-tracking', label: 'Stage Tracker',  icon: GitBranch },
      { key: 'time_report',      to: '/worker/time-report',      label: 'Time Report',    icon: Clock },
      { key: 'daily_progress',   to: '/worker/daily-progress',   label: 'Daily Progress', icon: Calendar },
      { key: 'materials',        to: '/worker/materials',        label: 'Materials',      icon: Layers },
    ]
  },
  {
    group: 'Operations',
    menus: [
      { key: 'packing',          to: '/worker/packing',          label: 'Packing',        icon: Package },
      { key: 'barcodes',         to: '/worker/barcodes',         label: 'Barcodes',       icon: ScanBarcode },
      { key: 'dispatch',         to: '/worker/dispatch',         label: 'Dispatch',       icon: Truck },
      { key: 'outsource',        to: '/worker/outsource',        label: 'Outsource',      icon: Truck },
    ]
  },
  {
    group: 'Sales & Finance',
    menus: [
      { key: 'sales',            to: '/worker/sales',            label: 'Sales',          icon: ShoppingCart },
      { key: 'sale_challans',    to: '/worker/sale-challans',    label: 'Sale Challans',  icon: FileText },
      { key: 'client_pos',       to: '/worker/client-pos',       label: 'Client POs',     icon: Inbox },
    ]
  },
  {
    group: 'Support',
    menus: [
      { key: 'client_support',   to: '/worker/client-support',  label: 'Client Support', icon: Headphones },
      { key: 'queries',          to: '/worker/queries-admin',    label: 'Admin Queries',  icon: MessageSquare },
      { key: 'terms',            to: '/worker/terms',            label: 'Terms & Conditions', icon: ScrollText },
      { key: 'settings',         to: '/worker/settings',         label: 'Settings',       icon: Settings },
    ]
  },
];

// Flat list for desktop nav
const ALL_PERM_MENUS = PERM_MENU_GROUPS.flatMap(g => g.menus);

export default function WorkerLayout({ licenseInfo }) {
  const { secondsLeft, isIdle } = useInactivityLogout();
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // CFT sheets assigned to this worker (shown in the menu only when there are some)
  const [myCftCount, setMyCftCount] = useState(0);
  useEffect(() => {
    api.get('/cft-assigned').then(r => setMyCftCount((r.data || []).length)).catch(() => {});
  }, [location.pathname]);

  // Always-visible items
  const alwaysItems = [
    { to: '/worker',              label: 'Dashboard',   icon: LayoutDashboard, end: true },
    { to: '/worker/my-tasks',     label: 'My Tasks',    icon: ClipboardList },
    { to: '/worker/queries',      label: 'Queries',     icon: MessageSquare },
    { to: '/worker/my-approvals', label: 'My Approvals', icon: CheckSquare },
    ...(myCftCount ? [{ to: '/worker/my-cft', label: 'My CFT Sheets', icon: Ruler }] : []),
  ];

  // Permission-gated flat list for desktop
  const permItems = ALL_PERM_MENUS.filter(m => hasPermission(m.key, 'read'));
  const desktopNavItems = [...alwaysItems, ...permItems];

  // For mobile bottom bar — always-visible 3 items + hamburger
  const mobileFixedItems = alwaysItems.slice(0, 4);   // bottom bar has room for 4; the rest are in the menu

  // Grouped permission items for drawer
  const drawerGroups = PERM_MENU_GROUPS
    .map(g => ({ ...g, menus: g.menus.filter(m => hasPermission(m.key, 'read')) }))
    .filter(g => g.menus.length > 0);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">

      {/* ── TOP HEADER ── */}
      <header className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-4 shadow-lg sticky top-0 z-40">

        {/* Logo + Name */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <img src="/Logo.png" alt="Logo" className="w-8 h-8 rounded-lg object-contain flex-shrink-0 bg-white p-0.5" />
          <span className="font-bold text-xs sm:text-sm truncate max-w-[110px] sm:max-w-none">
            {settings.company_name || 'MOZI INNOVATORS LLP'}
          </span>
        </div>

        {/* Desktop Nav — scrollable */}
        <nav className="hidden sm:flex gap-1 flex-1 overflow-x-auto scrollbar-none">
          {desktopNavItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors whitespace-nowrap flex-shrink-0 ${
                  isActive ? 'bg-amber-500 text-white' : 'text-slate-300 hover:bg-slate-700'
                }`
              }
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2 sm:gap-3 ml-auto flex-shrink-0">
          {isIdle && (
            <div className={`hidden sm:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${
              secondsLeft <= 60 ? 'bg-red-50 text-red-600 border-red-200 animate-pulse'
              : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}>
              <span>⏱</span>
              <span>{secondsLeft <= 60 ? `${secondsLeft}s` : `${Math.floor(secondsLeft/60)}m`}</span>
            </div>
          )}

          <NotificationBell />

          {/* User avatar */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-amber-500 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {user?.name?.charAt(0)}
            </div>
            <div className="hidden sm:block min-w-0">
              <p className="text-white text-xs font-medium leading-tight truncate max-w-[100px]">{user?.name}</p>
              <p className="text-slate-400 text-[10px]">Worker</p>
            </div>
          </div>

          {/* Desktop logout */}
          <button onClick={handleLogout}
            className="hidden sm:flex p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-red-400 transition-colors"
            title="Logout">
            <LogOut className="w-4 h-4" />
          </button>

          {/* Mobile hamburger — opens drawer */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="sm:hidden p-1.5 hover:bg-slate-700 rounded-lg text-slate-300 transition-colors"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* ── MAIN CONTENT ── */}
      <main className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto pb-20 sm:pb-4">
        <Outlet />
        <FestivalAmbient />
      </main>

      {/* ── MOBILE BOTTOM BAR — only always-visible 3 items ── */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900 border-t border-slate-700 shadow-2xl">
        <div className="flex items-stretch">
          {mobileFixedItems.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 flex-1 text-center transition-colors ${
                  isActive
                    ? 'text-amber-400 border-t-2 border-amber-400'
                    : 'text-slate-400 border-t-2 border-transparent'
                }`
              }
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}

          {/* More button — opens drawer */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 flex-1 text-slate-400 border-t-2 border-transparent transition-colors active:text-amber-400"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* ── MOBILE DRAWER OVERLAY ── */}
      {drawerOpen && (
        <div className="sm:hidden fixed inset-0 z-50 flex">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />

          {/* Drawer panel — slides in from left */}
          <div className="relative w-72 max-w-[85vw] bg-slate-900 h-full flex flex-col shadow-2xl animate-slide-in-left">

            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-slate-700">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                  {user?.name?.charAt(0)}
                </div>
                <div>
                  <p className="text-white text-sm font-semibold leading-tight">{user?.name}</p>
                  <p className="text-slate-400 text-xs">Worker</p>
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-1.5 hover:bg-slate-700 rounded-lg text-slate-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer nav — scrollable */}
            <div className="flex-1 overflow-y-auto py-3">

              {/* Always-visible items */}
              <div className="px-3 mb-4">
                {alwaysItems.map(({ to, label, icon: Icon, end }) => (
                  <NavLink key={to} to={to} end={end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 transition-colors ${
                        isActive
                          ? 'bg-amber-500 text-white'
                          : 'text-slate-300 hover:bg-slate-700'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{label}</span>
                    {({ isActive }) => isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </NavLink>
                ))}
              </div>

              {/* Permission-gated groups */}
              {drawerGroups.map(group => (
                <div key={group.group} className="px-3 mb-4">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider px-3 mb-1.5">
                    {group.group}
                  </p>
                  {group.menus.map(({ to, label, icon: Icon }) => (
                    <NavLink key={to} to={to}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 transition-colors ${
                          isActive
                            ? 'bg-amber-500 text-white'
                            : 'text-slate-300 hover:bg-slate-700'
                        }`
                      }
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm font-medium">{label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}

              {/* Empty state when no extra permissions */}
              {drawerGroups.length === 0 && permItems.length === 0 && (
                <div className="px-6 py-4 text-slate-500 text-xs text-center">
                  No additional menus assigned.
                </div>
              )}
            </div>

            {/* Drawer footer — logout */}
            <div className="px-3 py-4 border-t border-slate-700">
              {isIdle && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl mb-2 text-xs font-semibold ${
                  secondsLeft <= 60 ? 'bg-red-900/50 text-red-300' : 'bg-amber-900/50 text-amber-300'
                }`}>
                  <span>⏱</span>
                  <span>Auto-logout in {secondsLeft <= 60 ? `${secondsLeft}s` : `${Math.floor(secondsLeft/60)}m ${secondsLeft%60}s`}</span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-red-400 hover:bg-red-900/30 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
